import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { AppState, DialogState, NotificationState } from './types';
import { RootState } from './store';
import { AuthenticateAdminAsync, AuthenticateApplicantAsync } from '../features/authentication/authSlice';
import { createApplicantAsync } from '../features/admin/ApplicantsView/applicantsSlice';
import { createCategoryAsync, updateCategoryAsync } from '../features/admin/questions/CategoriesView/categorySlice';
import { createQuestionAsync, updateQuestionAsync } from '../features/admin/questions/QuestionsView/questionSlice';
import { createAnswerAsync, destroyAnswerAsync, updateAnswerAsync } from '../features/applicant/TestView/answerSlice';

const sliceName = 'app';

const initialState: AppState = {
  notification: null,
  dialog: { open: false },
  pageName: ''
};

const appSlice = createSlice({
  name: sliceName,
  initialState: initialState,
  reducers: {
    setPageName: (state: AppState, action: PayloadAction<string>): AppState => {
      return { ...state, pageName: action.payload };
    },
    setDialog: (state: AppState, action: PayloadAction<DialogState>): AppState => {
      return { ...state, dialog: action.payload };
    },
    closeDialog: (state: AppState): AppState => {
      return { ...state, dialog: { open: false } };
    },
    setNotification: (state: AppState, action: PayloadAction<NotificationState>): AppState => {
      return { ...state, notification: action.payload };
    }
  },
  extraReducers: {
    [AuthenticateApplicantAsync.rejected.type]: (state: AppState, action: PayloadAction<string>): AppState => {
      return { ...state, notification: { message: action.payload, type: 'error' } };
    },
    [AuthenticateAdminAsync.rejected.type]: (state: AppState, action: PayloadAction<string>): AppState => {
      return { ...state, notification: { message: action.payload, type: 'error' } };
    },
    [createApplicantAsync.fulfilled.type]: (state: AppState): AppState => {
      return { ...state, notification: { message: 'New applicant added!', type: 'success' } };
    },
    [createApplicantAsync.rejected.type]: (state: AppState, action: PayloadAction<string>): AppState => {
      return { ...state, notification: { message: action.payload, type: 'error' } };
    },
    [createCategoryAsync.fulfilled.type]: (state: AppState): AppState => {
      return { ...state, notification: { message: 'New category added!', type: 'success' } };
    },
    [createCategoryAsync.rejected.type]: (state: AppState, action: PayloadAction<string>): AppState => {
      return { ...state, notification: { message: action.payload, type: 'error' } };
    },
    [updateCategoryAsync.fulfilled.type]: (state: AppState): AppState => {
      return { ...state, notification: { message: 'Category updated!', type: 'success' } };
    },
    [updateCategoryAsync.rejected.type]: (state: AppState, action: PayloadAction<string>): AppState => {
      return { ...state, notification: { message: action.payload, type: 'error' } };
    },
    [createQuestionAsync.fulfilled.type]: (state: AppState): AppState => {
      return { ...state, notification: { message: 'New Question added!', type: 'success' } };
    },
    [createQuestionAsync.rejected.type]: (state: AppState, action: PayloadAction<string>): AppState => {
      return { ...state, notification: { message: action.payload, type: 'error' } };
    },
    [updateQuestionAsync.fulfilled.type]: (state: AppState): AppState => {
      return { ...state, notification: { message: 'Question updated!', type: 'success' } };
    },
    [updateQuestionAsync.rejected.type]: (state: AppState, action: PayloadAction<string>): AppState => {
      return { ...state, notification: { message: action.payload, type: 'error' } };
    },
    [createAnswerAsync.fulfilled.type]: (state: AppState): AppState => {
      return { ...state, notification: { message: 'Answer successfully submitted!', type: 'success' } };
    },
    [updateAnswerAsync.fulfilled.type]: (state: AppState): AppState => {
      return { ...state, notification: { message: 'Answer successfully updated!', type: 'success' } };
    },
    [destroyAnswerAsync.fulfilled.type]: (state: AppState): AppState => {
      return { ...state, notification: { message: 'Answer successfully cleared!', type: 'success' } };
    }
  }
});

export const selectApp = (state: RootState): AppState => state.app;
export const { setPageName, setDialog, closeDialog, setNotification } = appSlice.actions;
export default appSlice.reducer;
